import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";

export default function AdminSettings() {
  const navigate = useNavigate();

  // SMTP Settings
  const [smtpEmail, setSmtpEmail] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpHost, setSmtpHost] = useState("smtp.gmail.com");
  const [smtpPort, setSmtpPort] = useState("587");
  const [adminEmail, setAdminEmail] = useState("");

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [activeTemplate, setActiveTemplate] = useState(null);

  // Email Logs
  const [emailLogs, setEmailLogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("smtp");

  // Check admin auth
  useEffect(() => {
    if (!localStorage.getItem("isAdminLoggedIn")) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Load data
  useEffect(() => {
    loadSettings();
    loadTemplates();
    loadEmailLogs();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/settings`);
      const data = await res.json();
      if (data.success) {
        setSmtpEmail(data.settings.smtp_email || "");
        setSmtpPassword(data.settings.smtp_password || "");
        setSmtpHost(data.settings.smtp_host || "smtp.gmail.com");
        setSmtpPort(data.settings.smtp_port || "587");
        setAdminEmail(data.settings.admin_email || "");
      }
    } catch (e) {
      console.error("Failed to load settings", e);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/templates`);
      const data = await res.json();
      if (data.success) {
        setTemplates(data.templates);
        const active = data.templates.find((t) => t.is_active);
        setActiveTemplate(active || null);
      }
    } catch (e) {
      console.error("Failed to load templates", e);
    }
  };

  const loadEmailLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/email-logs`);
      const data = await res.json();
      if (data.success) {
        setEmailLogs(data.logs);
      }
    } catch (e) {
      console.error("Failed to load logs", e);
    }
  };

  // Save SMTP settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/update-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          smtp_email: smtpEmail,
          smtp_password: smtpPassword,
          smtp_host: smtpHost,
          smtp_port: smtpPort,
          admin_email: adminEmail,
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Settings saved successfully!");
      } else {
        alert("❌ Failed to save settings");
      }
    } catch {
      alert("❌ Server error");
    } finally {
      setSaving(false);
    }
  };

  // Upload template
  const handleUploadTemplate = async () => {
    if (!templateFile) {
      alert("Please select a template image");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("template_image", templateFile);
    formData.append("template_name", templateName || "Certificate Template");

    try {
      const res = await fetch(`${API_URL}/upload-template`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Template uploaded and set as active!");
        setTemplateFile(null);
        setTemplateName("");
        loadTemplates();
      } else {
        alert("❌ Failed to upload template");
      }
    } catch {
      alert("❌ Server error");
    } finally {
      setUploading(false);
    }
  };

  // Set active template
  const handleSetActive = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/set-active-template/${templateId}`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Template activated");
        loadTemplates();
      }
    } catch {
      alert("❌ Server error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">⚙️ Admin Settings</h1>
            <p className="text-sm text-slate-500">Configure email, templates, and view logs</p>
          </div>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="px-4 py-2 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-100 transition text-sm"
          >
            ← Dashboard
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "smtp", label: "📧 Email Settings", icon: "📧" },
            { id: "template", label: "🖼️ Certificate Template", icon: "🖼️" },
            { id: "logs", label: "📋 Email Logs", icon: "📋" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition ${
                activeTab === tab.id
                  ? "bg-indigo-100 text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* SMTP Settings */}
        {activeTab === "smtp" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">📧 Email Configuration</h2>
            <p className="text-sm text-slate-500 mb-6">
              Configure SMTP settings to automatically send certificates to students upon approval.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-600">SMTP Host</label>
                <input
                  type="text"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-600">SMTP Port</label>
                <input
                  type="text"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="587"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-600">SMTP Email</label>
                <input
                  type="email"
                  value={smtpEmail}
                  onChange={(e) => setSmtpEmail(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="noreply@yourdomain.com"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-600">SMTP Password</label>
                <input
                  type="password"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="App password or SMTP password"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-600">Admin Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="admin@smartgonext.com"
                />
              </div>
            </div>

            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-medium mb-1">💡 Using Gmail?</p>
              <p>
                Enable 2-Factor Authentication and generate an App Password for SMTP.
                Use that App Password here. Never use your regular Gmail password.
              </p>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="mt-6 px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-indigo-800 transition disabled:opacity-50 shadow-sm"
            >
              {saving ? "Saving..." : "💾 Save Email Settings"}
            </button>
          </div>
        )}

        {/* Template Management */}
        {activeTab === "template" && (
          <div className="space-y-6">
            {/* Upload */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">🖼️ Upload Certificate Template</h2>
              <p className="text-sm text-slate-500 mb-6">
                Upload a certificate background template. Text will be overlaid automatically.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-slate-600">Template Name</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="My Certificate Template"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-slate-600">Template Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setTemplateFile(e.target.files[0])}
                    className="w-full border border-slate-300 rounded-xl p-2 bg-slate-50 text-sm"
                  />
                </div>
              </div>

              {templateFile && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                  ✅ Selected: {templateFile.name}
                </div>
              )}

              <button
                onClick={handleUploadTemplate}
                disabled={uploading || !templateFile}
                className="mt-6 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition disabled:opacity-50 shadow-sm"
              >
                {uploading ? "Uploading..." : "📤 Upload & Set Active"}
              </button>
            </div>

            {/* Active Template Preview */}
            {activeTemplate && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">✅ Active Template</h3>
                <div className="flex items-center gap-4">
                  <img
                    src={activeTemplate.image_url}
                    alt={activeTemplate.template_name}
                    className="w-48 h-32 object-cover rounded-xl border border-slate-200 shadow-sm"
                  />
                  <div>
                    <p className="font-medium text-slate-800">{activeTemplate.template_name}</p>
                    <p className="text-sm text-slate-500">
                      Uploaded: {activeTemplate.uploaded_at}
                    </p>
                    <span className="inline-block mt-2 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* All Templates */}
            {templates.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">📦 All Templates</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      className={`relative border rounded-xl p-3 ${
                        t.is_active ? "border-green-300 bg-green-50" : "border-slate-200"
                      }`}
                    >
                      <img
                        src={t.image_url}
                        alt={t.template_name}
                        className="w-full h-24 object-cover rounded-lg mb-2"
                      />
                      <p className="text-sm font-medium text-slate-700 truncate">{t.template_name}</p>
                      <p className="text-xs text-slate-400">{t.uploaded_at}</p>
                      {t.is_active ? (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetActive(t.id)}
                          className="mt-1 text-xs text-indigo-600 hover:underline font-medium"
                        >
                          Set Active
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Email Logs */}
        {activeTab === "logs" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">📋 Email Logs</h2>
            <p className="text-sm text-slate-500 mb-6">
              View all certificate emails sent to students.
            </p>

            {emailLogs.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <p className="text-4xl mb-3">📭</p>
                <p>No emails sent yet</p>
                <p className="text-sm mt-1">Emails are sent automatically when certificates are approved</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Cert ID</th>
                      <th className="px-4 py-3 text-left">Recipient</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Sent At</th>
                      <th className="px-4 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {emailLogs.map((log, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs">{log.cert_id}</td>
                        <td className="px-4 py-3">{log.recipient_email}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                            {log.email_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{log.sent_at}</td>
                        <td className="px-4 py-3 text-center">
                          {log.status === "sent" ? (
                            <span className="text-green-600 font-medium">✅ Sent</span>
                          ) : (
                            <span className="text-red-600 font-medium">❌ Failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}